<template>
    <div id="manage-users" class="relative h-full flex flex-col h-full p-4">
        <div>
            <div class="flex justify-between items-center h-10">
                <h1 class="m-0 truncate">{{ $t('manage_school_users_header') }}</h1>
                <XButton
                    @click="showManageInviteUser = true"
                    :disabled="disableAll || loading"
                    class="shadow-text-none"
                    bg-color="#28a745"
                >
                    <i class="fas fa-plus"></i>
                    <span class="hidden xl:inline">{{ $t('manage_school_users_button_invite') }}</span>
                </XButton>
            </div>
            <ManageSearch
                @search="onSearch($event)"
                :disabled-search="disableAll || loading"
                ref="manageSearch"
                class="mt-4"
            />
        </div>
        <div id="users-section" class="relative flex-1 overflow-hidden">
            <ManageTable
                @pagination-change="onPageChange"
                :list="users"
                :current-page="currentPage"
                :page-count="pageCount"
                :items-per-page="itemsPerPage"
                :total-items="totalItems"
                :loading="loading"
                :disable-pagination="disableAll"
            >
                <template #header>
                    <h3 class="w-2/5 m-0 font-size-16">{{ $t('manage_school_table_header_name') }}</h3>
                    <h3 class="w-1/5 m-0 font-size-16 text-center">
                        {{ $t('manage_school_users_table_header_roles') }}
                    </h3>
                    <h3 class="w-1/5 m-0 font-size-16 text-center">
                        {{ $t('manage_school_users_table_header_status') }}
                    </h3>
                    <h3 class="w-1/5 m-0 font-size-16 text-right">
                        {{ $t('manage_school_users_table_header_actions') }}
                    </h3>
                </template>
                <template v-slot="{ item, index }">
                    <div class="user-detail flex items-center w-full xl:w-2/5">
                        <div class="avatar flex justify-center items-center rounded-full overflow-hidden">
                            <img
                                v-if="item.image_profile"
                                :src="item.image_profile"
                                class="w-full h-full object-cover"
                            />
                            <i v-else class="fas fa-user text-white"></i>
                        </div>
                        <div class="flex-1 overflow-hidden">
                            <div class="name flex items-center text-sm">
                                <span class="truncate">{{ item.name }}</span>
                                <span v-if="item._id.$oid === you.id" class="text-green">
                                    ({{ $t('manage_school_users_table_you') }})
                                </span>
                            </div>

                            <div class="email text-xs truncate">
                                {{ item.email }}
                            </div>
                        </div>
                    </div>
                    <div class="flex xl:justify-center items-center w-full xl:w-1/5">
                        <div
                            v-if="item.is_school_admin"
                            class="flex justify-center items-center rounded-full bg-purple px-2"
                        >
                            <span class="text-xs text-white">{{ $t('manage_school_users_table_role_admin') }}</span>
                        </div>
                        <div
                            :class="{
                                'flex justify-center items-center ml-1 rounded-full px-2': true,
                                'bg-green': ['teacher', 'student'].includes(item.role),
                                'bg-yellow-dark': ['teacher_plus', 'student_plus'].includes(item.role)
                            }"
                        >
                            <span v-if="item.role === 'teacher'" class="text-xs text-white">
                                {{ $t('school_role_teacher_basic') }}
                            </span>
                            <span v-if="item.role === 'teacher_plus'" class="text-xs text-white">
                                {{ $t('school_role_teacher_plus') }}
                            </span>
                            <span v-if="item.role === 'student'" class="text-xs text-white">
                                {{ $t('school_role_student_basic') }}
                            </span>
                            <span v-if="item.role === 'student_plus'" class="text-xs text-white">
                                {{ $t('school_role_student_plus') }}
                            </span>
                        </div>
                    </div>
                    <div class="w-full xl:w-1/5">
                        <div
                            :class="{
                                'text-sm xl:text-center': true
                            }"
                        >
                            <span class="xl:hidden">{{ $t('manage_school_users_table_status') }}: </span>
                            <span
                                :class="{
                                    'font-bold text-center': true,
                                    'text-red': item.status === 'suspended',
                                    'text-green': item.status === 'active',
                                    'text-grey': !item.status
                                }"
                            >
                                <template v-if="item.status === 'suspended'">
                                    {{ $t('manage_school_users_table_suspended') }}
                                </template>
                                <template v-else-if="item.status === 'active'">
                                    {{ $t('manage_school_users_table_active') }}
                                </template>
                                <template v-else>
                                    {{ $t('manage_school_users_table_unknown') }}
                                </template>
                            </span>
                        </div>
                    </div>
                    <div class="settings flex justify-end w-full xl:w-1/5">
                        <div v-if="item._id.$oid !== you.id" class="flex w-full xl:w-auto">
                            <XButton
                                v-if="!item.is_school_admin && ['teacher_plus', 'teacher'].includes(item.role)"
                                v-tippy="{ placement: 'top' }"
                                @click="assignAdmin(item)"
                                :content="$t('manage_school_settings_button_assign_admin')"
                                :class="`assign-admin-button-${index} flex-1 ml-auto shadow-text-none`"
                                :disabled="item.assignLoading || disableAll"
                                bg-color="#f2d024"
                            >
                                <span v-if="item.assignLoading" class="close-loader"></span>
                                <i v-else class="fas fa-user-tie"></i>
                            </XButton>
                            <XButton
                                v-tippy="{ placement: 'top' }"
                                @click="suspendUser(item, item.status === 'suspended' ? false : true)"
                                :content="
                                    item.status === 'suspended'
                                        ? $t('manage_school_quiz_table_tippy_active')
                                        : $t('manage_school_quiz_table_tippy_suspend')
                                "
                                :class="`suspend-user-button-${index} flex-1 ml-auto shadow-text-none`"
                                :disabled="item.suspendLoading || disableAll"
                                :bg-color="item.status === 'suspended' ? '#28a745' : '#dc3545'"
                            >
                                <span v-if="item.suspendLoading" class="close-loader"></span>
                                <template v-else>
                                    <i v-if="item.status === 'suspended'" class="fas fa-lock-open"></i>
                                    <i v-else class="fas fa-lock"></i>
                                </template>
                            </XButton>
                        </div>
                    </div>
                </template>
                <template #empty>
                    <div class="flex justify-center items-center w-full h-full">
                        <div class="flex flex-col justify-center items-center max-w-xl">
                            <i class="fas fa-street-view"></i>
                            <h4
                                v-html="
                                    keyword
                                        ? $t('manage_school_users_empty_state_keyword', { keyword })
                                        : $t('manage_school_users_empty_state')
                                "
                                class="text-center m-0 mt-4"
                            ></h4>
                        </div>
                    </div>
                </template>
                <template #loading>
                    <InnerLoader color="#333" class="pin-t pin-l" />
                </template>
            </ManageTable>
        </div>
        <ManageInviteUser
            v-if="showManageInviteUser"
            @close="showManageInviteUser = false"
            class="absolute pin-t pin-l w-full z-10"
        />
    </div>
</template>

<script>
import { currentUser } from '@/lib/user'
import popup from '@/lib/popup.js'
import ManageSearch from '@/components/manage-school/ManageSearch'
import ManageInviteUser from '@/components/manage-school/ManageInviteUser'
import XButton from '@/components/v2/XButton'
import ManageTable from '@/components/manage-school/ManageTable'
import InnerLoader from '@/components/InnerLoader'
import { load } from 'recaptcha-v3'

export default {
    components: {
        ManageSearch,
        ManageInviteUser,
        XButton,
        ManageTable,
        InnerLoader
    },

    data() {
        return {
            showManageInviteUser: false,
            currentPage: 1,
            pageCount: 0,
            itemsPerPage: 0,
            totalItems: 0,
            keyword: '',
            users: null,
            loading: false,
            disableAll: false
        }
    },

    created() {
        this.fetchSchoolUser()
    },

    computed: {
        you() {
            return currentUser()
        }
    },

    methods: {
        onSearch(keyword) {
            if (this.keyword === keyword) return

            this.fetchSchoolUser(keyword)
        },

        async fetchSchoolUser(keyword) {
            if (this.keyword === keyword) return

            this.loading = true
            this.disableAll = true

            this.users = null

            const urlParams = new URL(window.location.href)

            if (typeof keyword !== 'undefined') {
                this.keyword = keyword
                this.currentPage = 1

                urlParams.searchParams.delete('page')

                window.history.replaceState(null, null, urlParams.toString())
            }

            this.currentPage = urlParams.searchParams.get('page') || 1

            try {
                const res = await this.$store.dispatch('getUserInSchool', {
                    params: {
                        keyword: this.keyword,
                        page: this.currentPage
                    }
                })

                this.users = res.data.data.map(user => {
                    return {
                        ...user,
                        assignLoading: false,
                        suspendLoading: false
                    }
                })

                this.currentPage = res.data.current_page
                this.pageCount = res.data.page_count
                this.itemsPerPage = res.data.perpage
                this.totalItems = res.data.total
            } catch {
                this.users = []
                this.toastError(this.$t('manage_school_popup_error'))
            } finally {
                this.loading = false
                this.disableAll = false
            }
        },

        onPageChange(value) {
            this.currentPage = value

            const urlParams = new URL(window.location.href)

            urlParams.searchParams.set('page', this.currentPage)

            window.history.replaceState(null, null, urlParams.toString())

            this.fetchSchoolUser()
        },

        assignAdmin(user) {
            if (user._id.$oid === this.you.id || this.disableAll || user.assignLoading) return

            popup.confirm(this.$t('manage_school_users_confirm_assign_admin'), async () => {
                this.disableAll = true
                user.assignLoading = true

                try {
                    const recaptcha = await load(process.env.VUE_APP_RECAPTCHA_SITE_KEY, {
                        useRecaptchaNet: true,
                        autoHideBadge: true
                    })

                    const token = await recaptcha.execute('validate_captcha', {
                        action: 'validate_captcha'
                    })

                    await this.$store.dispatch('updateSchoolAdminStatus', {
                        user_id: user._id.$oid,
                        status: 'assign',
                        captcha_response: token
                    })

                    this.toastSuccess(this.$t('manage_school_users_popup_assign_admin_complete'))

                    this.fetchSchoolUser()
                } catch {
                    this.toastError(this.$t('manage_school_popup_error'))
                    user.assignLoading = false
                } finally {
                    this.disableAll = false
                }
            })
        },

        suspendUser(user, suspend) {
            if (user._id.$oid === this.you.id || this.disableAll || user.suspendLoading) return

            popup.confirm(
                suspend
                    ? this.$t('manage_school_users_confirm_suspend_user')
                    : this.$t('manage_school_users_confirm_active_user'),
                async () => {
                    this.disableAll = true
                    user.suspendLoading = true

                    try {
                        await this.$store.dispatch('updateSchoolUserStatus', {
                            user_id: user._id.$oid,
                            status: suspend ? 'suspend' : 'active'
                        })

                        if (suspend) {
                            this.toastSuccess(this.$t('manage_school_users_popup_suspend_user_complete'))
                        } else {
                            this.toastSuccess(this.$t('manage_school_users_popup_active_user_complete'))
                        }

                        this.fetchSchoolUser()
                    } catch {
                        this.toastError(this.$t('manage_school_popup_error'))
                        user.suspendLoading = false
                    } finally {
                        this.disableAll = false
                    }
                }
            )
        }
    }
}
</script>

<style lang="scss">
#manage-users {
    row-gap: 1rem;

    #users-section {
        .manage-table-item {
            row-gap: 1rem;

            .user-detail {
                column-gap: 1rem;

                .avatar {
                    width: 2rem;
                    height: auto !important;
                    aspect-ratio: 1 / 1;
                    background-color: gray;
                }

                .name {
                    column-gap: 0.3rem;
                }
            }

            .settings {
                > div {
                    column-gap: 0.5rem;

                    button {
                        width: 100%;
                        height: 2rem;

                        @media screen and (min-width: 1200px) {
                            width: 2rem;
                            height: auto;
                            padding: 0;
                            aspect-ratio: 1 / 1;
                        }
                    }
                }
            }
        }
    }
}
</style>